import AIcon from '@/components/A/AIcon/AIcon'
import styles from './AnalysisBlock.module.less'
import cls from 'classnames'
import { useEffect, useRef, useState } from 'react'
import { commonUtils } from '@/helpers/commonUtils'
import { SubjectiveResponseCurveVideo } from '@/pages/ProjectDetailPage/components/VideoAnalysis/VideoAnalysisChart/VideoAnalysisChart'
import MiddleText from '@/components/MiddleText/MiddleText'
import { useTranslation } from 'react-i18next'
import Typed from 'typed.js'
import ImagePreview from '@/components/ImagePreview/ImagePreview'
import { isPortraitByProjectInfo } from '@/helpers/productCategory'
import { getSafeFactorScore, SCORE_PLACEHOLDER } from '../scoreUtils'

type Status = 'pending' | 'completed'

const PORTRAIT_SPLASH_VIDEO_WIDTH = 300
const PORTRAIT_SPLASH_CHART_WIDTH = PORTRAIT_SPLASH_VIDEO_WIDTH + 28 + 15

export interface AnalysisBlockProps {
  title: string
  content: Array<{ title: string, status: Status }>
  status: Status
}


const TypedText = (props: { text: string }) => {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const typed = new Typed(ref.current, {
      strings: [props.text + ' '],
      cursorChar: '',
      fadeOut: true,
      typeSpeed: 5
    })
    return () => {
      typed.destroy()
    }
  }, [props.text])
  return <div className={styles.typedText} ref={ref}></div>
}

export const AnalysisTipBlock = (props: AnalysisBlockProps) => {
  const { title, content, status } = props

  return <div className={styles.analysisBlock}>
    <div className={cls(styles.title)}>
      <span className={styles.iconBox}>
        <span className={cls(styles.statusIcon, status === 'pending' ? styles.showPending : styles.hideIcon)}>
          <AIcon className={styles.pending} type="icon-line-global_loading" width={20} height={20}></AIcon>
        </span>
        <span className={cls(styles.statusIcon, status === 'completed' ? styles.showCompleted : styles.hideIcon)}>
          <AIcon type="icon-fill-global_check2" width={20} height={20}></AIcon>
        </span>
      </span>
      <span className={cls(status === 'pending' && styles.pendingTitle)}>{title}</span>
    </div>
    {
      content.length > 0 && (
        <div className={styles.content}>
          {content.map((item, index) => (
            <TypedText key={index} text={item.title} />
          ))}
        </div>
      )
    }
  </div>
}


export interface AnalysisResultBlockProps {
  title: React.ReactNode
  type: 'media' | 'end'
  content: React.ReactNode
  titleExtra?: React.ReactNode
}

export const AnalysisResultBlock = (props: AnalysisResultBlockProps) => {
  const { title, type, content, titleExtra } = props
  return <div className={styles.analysisBlock}>
    <div className={styles.title}>
      {
        type === 'media' &&
        <img className={styles.media} src={commonUtils.getImagePath('projectDetailPage/ai-block-result-icon.png')} alt="" />
      }
      {type === 'end' &&
        <AIcon type="icon-fill-global_check2" width={20} height={20}></AIcon>
      }
      <span className={styles.titleText}>{title}</span>
      {titleExtra ? <div className={styles.titleExtra}>{titleExtra}</div> : null}
    </div>
    <div className={cls(styles.content, styles.results)}>
      {/* <AnimationPlaceholder delay={1000}> */}
      {content}
      {/* </AnimationPlaceholder> */}
    </div>
  </div>
}


/** 热力图展示模块 */
interface HeatMapBlockProps {
  video: string
  pics: string[]
  isPortraitMode?: boolean
  isCollapsed?: boolean
  onExpand?: () => void
  onToggle?: () => void
}
export const HeatMapBlock = (props: HeatMapBlockProps) => {
  const { video, pics, isPortraitMode = false, isCollapsed = true, onExpand, onToggle } = props
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [previewIndex, setPreviewIndex] = useState(0)
  const { t } = useTranslation()
  const handleExpand = () => onExpand?.()
  const FLOW_ANALYSIS_PREFIX = 'dashboard.experience.projectDetailPage.flowAnalysis'
  return <div className={styles.heatMapBlock}>
    <div className={cls(styles.mediaRow, isPortraitMode && styles.mediaRowPortrait)}>
      <div className={cls(styles.videoBox, isPortraitMode && styles.videoBoxPortrait)}>
        <video className={cls(isPortraitMode && styles.videoPortrait)} src={video} controls></video>
      </div>
      <div className={cls(
        styles.picBox,
        isPortraitMode && styles.picBoxPortrait,
        { [styles.collapsed]: isCollapsed }
      )}>
        {
          pics.map((pic, index) => (
            <div
              key={index}
              className={isPortraitMode ? styles.picItemPortrait : styles.picItem}
              onClick={() => {
                setPreviewIndex(index)
                setIsPreviewOpen(true)
                handleExpand()
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  setPreviewIndex(index)
                  setIsPreviewOpen(true)
                  handleExpand()
                }
              }}
              role="button"
              tabIndex={0}
            >
              <img className={isPortraitMode ? styles.picImagePortrait : undefined} src={pic} alt="" />
            </div>
          ))
        }
      </div>
    </div>
    {!isPortraitMode ? (
      <div className={styles.collapseBtnBox}>
        <div className={styles.collapseBtn} onClick={onToggle}>
          <span>
            {isCollapsed
              ? t(`${FLOW_ANALYSIS_PREFIX}.terminology.thumbExpand`)
              : t(`${FLOW_ANALYSIS_PREFIX}.terminology.thumbCollapse`)
            }
          </span>
          <AIcon className={cls(styles.collapseIcon, { [styles.collapsed]: isCollapsed })} type="icon-line-global_down" width={12} height={12}></AIcon>
        </div>
      </div>
    ) : null}
    <ImagePreview
      images={pics}
      initialIndex={previewIndex}
      open={isPreviewOpen}
      onClose={() => setIsPreviewOpen(false)}
      onIndexChange={setPreviewIndex}
    />
  </div>
}

/** 主观响应曲线展示模块 */
interface SubjectiveResponseCurveBlockProps {
  project: ProjectModels.ProjectDetail | ProjectModels.ProjectDetailV3
}
export const SubjectiveResponseCurveBlock = (props: SubjectiveResponseCurveBlockProps) => {
  const { project } = props
  const { t } = useTranslation()
  const [viewMode, setViewMode] = useState<'realtime' | 'panoramic'>('realtime')
  const analysisBlocksRef = useRef<HTMLDivElement | null>(null)
  const adDuration = Number(project.adDuration || 0)
  const metricSeconds = project.detailMetrics.length
  const frameSeconds = project.frameInfos.length
  const inferredSecondCount = Math.max(
    metricSeconds,
    frameSeconds,
    Number.isFinite(adDuration) && adDuration > 0 ? Math.ceil(adDuration) : 0
  )
  const isPortraitMode = isPortraitByProjectInfo(project)
  const isSplashCreative =
    project.durationCategory === 'five_to_ten' ||
    (project.durationCategory !== 'ten_plus' &&
      inferredSecondCount > 0 &&
      inferredSecondCount <= 10)
  const realtimeLayout =
    isPortraitMode
      ? isSplashCreative ? 'portrait-splash' : 'portrait-regular'
      : 'default'
  return <div className={styles.subjectiveResponseCurveBlock} ref={analysisBlocksRef}>
    <div>
      <span className={styles.switchBtn}>
        <span className={cls(styles.viewBtn, viewMode === 'realtime' && styles.active)} onClick={() => {
          setViewMode('realtime')
        }}>
          <MiddleText text={t('dashboard.projectDetailPage.videoView.realtimeData')} />
        </span>
        <span className={cls(styles.viewBtn, viewMode === 'panoramic' && styles.active)} onClick={() => {
          setViewMode('panoramic')
        }}>
          <MiddleText text={t('dashboard.projectDetailPage.videoView.panoramicData')} />
        </span>
      </span>
    </div>
    <SubjectiveResponseCurveVideo
      videoBlobUrl={project.adUrl} viewMode={viewMode}
      clacSizeElement={() => analysisBlocksRef.current as HTMLElement}
      project={project}
      orientation={isPortraitMode ? 'portrait' : 'landscape'}
      layout={realtimeLayout}
      chartWidth={isSplashCreative ? PORTRAIT_SPLASH_CHART_WIDTH : undefined} />
  </div>
}


/** 消费者态度分析展示模块 */
interface Indicator {
  label: string
  index: number | string
  norm: number | string
  trend?: 'up' | 'down' | 'normal'
}

interface TableDataProps {
  titles: string[]
  indicators: Indicator[]
}

const formatIndicatorValue = (value: number | string) => {
  const numberValue = Number(value)
  if (!Number.isNaN(numberValue)) {
    return numberValue
  }
  return value
}

export const TableDataBlock = (props: TableDataProps) => {
  const { titles, indicators } = props

  return (
    <div className={styles.tableDataBlock}>
      <div className={cls(styles.row, styles.header)}>
        {titles.map((_title: string, _index: number) => (
          <div key={_index} className={cls(styles.cell, _index === 0 ? styles.label : styles.value)}>
            {_title}
          </div>
        ))}
      </div>
      {indicators.map((indicator, index) => {
        const trend = indicator.trend?.toLowerCase() as 'up' | 'down' | undefined
        return (
          <div key={`${indicator.label}-${index}`} className={styles.row}>
            <div className={cls(styles.cell, styles.label)}>{indicator.label}</div>
            <div className={cls(styles.cell, styles.value, styles.normValue, {
              [styles.positive]: trend === 'up',
              [styles.negative]: trend === 'down',
            })}>
              <div className={styles.valueContent}>
                {formatIndicatorValue(indicator.index)}
                <span className={styles.trendIcon}>
                  {trend === 'up' && <AIcon className={cls(styles.icon, styles.up)} type="icon-line-global_arrow" width={13} height={13}></AIcon>}
                  {trend === 'down' && <AIcon className={cls(styles.icon, styles.down)} type="icon-line-global_arrow" width={13} height={13}></AIcon>}
                </span>
              </div>
            </div>
            <div
              className={cls(styles.cell, styles.value, styles.normValue)} >
              {formatIndicatorValue(indicator.norm)}
            </div>
          </div>
        )
      })}
    </div>
  )
}


export const ResultBlock = (props: { project: ProjectModels.ProjectDetailV3 }) => {
  const { t } = useTranslation()
  const FLOW_ANALYSIS_PREFIX = 'dashboard.experience.projectDetailPage.flowAnalysis'
  const adeffIndex = getSafeFactorScore(props.project.dimensionFactors?.adeffIndex)
  const isPass = adeffIndex.isComparable && adeffIndex.score! >= adeffIndex.meanScore!
  const resultText = adeffIndex.isComparable
    ? (isPass ? t(`${FLOW_ANALYSIS_PREFIX}.terminology.result.pass`) : t(`${FLOW_ANALYSIS_PREFIX}.terminology.result.notPass`))
    : SCORE_PLACEHOLDER
  return <div className={cls(styles.tableDataBlock, styles.resultBlock)}>
    <div className={cls(styles.row, styles.header)}>
      <div className={cls(styles.cell, styles.label)}>{t(`${FLOW_ANALYSIS_PREFIX}.terminology.result.results`)}</div>
      <div className={cls(styles.cell, styles.value)}>{t(`${FLOW_ANALYSIS_PREFIX}.terminology.result.adeffScore`)}</div>
      <div className={cls(styles.cell, styles.value)}>{t(`${FLOW_ANALYSIS_PREFIX}.terminology.benchmark`)}</div>
    </div>
    <div className={styles.row}>
      <div className={cls(styles.cell, styles.label)}>
        <span className={cls(styles.tag, { [styles.pass]: isPass, [styles.notPass]: adeffIndex.isComparable && !isPass })}>
          {resultText}
        </span>
      </div>
      <div className={cls(styles.cell, styles.value)}>
        {adeffIndex.isComparable && (
          <img className={styles.bg} src={
            isPass
              ? commonUtils.getImagePath('projectDetailPage/score-pass-bg.png')
              : commonUtils.getImagePath('projectDetailPage/score-not-pass-bg.png')
          } alt="" />
        )}
        <span className={cls(styles.scoreValue, { [styles.pass]: isPass, [styles.notPass]: adeffIndex.isComparable && !isPass })}>
          {adeffIndex.displayScore}
        </span>
      </div>
      <div className={cls(styles.cell, styles.value, styles.normValue)} >{adeffIndex.displayMeanScore}</div>
    </div>
  </div>
}

interface AnimationPlaceholderProps {
  children: React.ReactNode
  delay?: number
  className?: string
  style?: React.CSSProperties
}

export const AnimationPlaceholder = ({
  children,
  delay = 0,
  className,
  style,
}: AnimationPlaceholderProps) => {
  const [showSkeleton, setShowSkeleton] = useState(delay > 0)
  const timerRef = useRef<number | null>(null)

  useEffect(() => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current)
      timerRef.current = null
    }
    if (delay > 0) {
      setShowSkeleton(true)
      timerRef.current = window.setTimeout(() => {
        setShowSkeleton(false)
      }, delay)
    } else {
      setShowSkeleton(false)
    }
    return () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }
  }, [delay])

  return (
    <div
      className={cls(styles.animationPlaceholder, className)}
      style={style}
    >
      <div
        className={cls(
          styles.animationPlaceholderContent,
          showSkeleton ? styles.hidden : styles.visible,
        )}
        style={!showSkeleton ? { animationDelay: `${delay}ms` } : undefined}
      >
        {children}
      </div>
      <div
        className={cls(
          styles.animationPlaceholderSkeleton,
          showSkeleton ? styles.visible : styles.hidden,
        )}
      />
    </div >
  )
}
